import { BooleanInput, Button, ColorPicker, Container, Element, Label, SelectInput, SliderInput, TextInput } from '@playcanvas/pcui';

import { Pose } from '../camera-poses';
import { i18n } from './localization';
import { Events } from '../events';
import { ExportType, SceneExportOptions } from '../file-handler';
import { AnimTrack, ExperienceSettings, defaultPostEffectSettings } from '../splat-serialize';
import sceneExport from './svg/export.svg';

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

const removeKnownExtension = (filename: string) => {
    // remove known extensions (ordered from longest to shortest for compound extensions)
    const knownExtensions = [
        '.compressed.ply',
        '.ksplat',
        '.splat',
        '.html',
        '.lcc2',
        '.ply',
        '.sog',
        '.spz',
        '.lcc',
        '.zip',
        '.json'
    ];

    for (let i = 0; i < knownExtensions.length; ++i) {
        const ext = knownExtensions[i];
        if (filename.endsWith(ext)) {
            return filename.slice(0, -ext.length);
        }
    }

    return filename;
};

const emptyThumbnail = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

const exportedThumbnailUrl = (url?: string) => {
    return url && url !== emptyThumbnail ? url : undefined;
};

type CameraThinningMode = 'none' | 'reduction' | 'target';

const thinCameraPoses = (poses: Pose[], mode: CameraThinningMode, reductionFactor: number, targetCount: number) => {
    if (mode === 'none' || poses.length <= 1) {
        return poses;
    }

    if (mode === 'reduction') {
        const factor = Math.max(1, Math.round(reductionFactor));
        const result = poses.filter((_, index) => index % factor === 0);
        // Keep the final camera so the exported sequence still covers the
        // complete input range when its length is not divisible by factor.
        if (result[result.length - 1] !== poses[poses.length - 1]) {
            result.push(poses[poses.length - 1]);
        }
        return result;
    }

    const count = Math.max(1, Math.min(poses.length, Math.round(targetCount)));
    if (count === 1) {
        return [poses[0]];
    }

    return Array.from({ length: count }, (_, index) => {
        const sourceIndex = Math.round(index * (poses.length - 1) / (count - 1));
        return poses[sourceIndex];
    });
};

class ExportPopup extends Container {
    show: (exportType: ExportType, splatNames: string[], showFilenameEdit: boolean) => Promise<null | SceneExportOptions>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, args = {}) {
        args = {
            id: 'export-popup',
            class: 'blocks-shortcuts',
            hidden: true,
            tabIndex: -1,
            ...args
        };

        super(args);

        // UI

        const dialog = new Container({
            id: 'dialog'
        });

        // header

        const header = new Container({
            id: 'header'
        });

        const headerText = new Label({
            id: 'header'
        });
        i18n.bindText(headerText, 'popup.export.header');

        header.append(createSvg(sceneExport, {
            id: 'icon'
        }));

        header.append(headerText);

        // content

        const content = new Container({ id: 'content' });

        // type

        const viewerTypeRow = new Container({
            class: 'row'
        });

        const viewerTypeLabel = new Label({
            class: 'label'
        });
        i18n.bindText(viewerTypeLabel, 'popup.export.type');

        const viewerTypeSelect = new SelectInput({
            class: 'select',
            defaultValue: 'html'
        });
        i18n.bindOptions(viewerTypeSelect, () => [
            { v: 'html', t: i18n.t('popup.export.html') },
            { v: 'zip', t: i18n.t('popup.export.package') }
        ]);

        viewerTypeRow.append(viewerTypeLabel);
        viewerTypeRow.append(viewerTypeSelect);

        const bundleFormatRow = new Container({ class: 'row' });
        bundleFormatRow.append(new Label({ class: 'label', text: 'Splat format' }));
        const bundleFormatSelect = new SelectInput({
            class: 'select',
            defaultValue: 'ply',
            options: [{ v: 'ply', t: 'PLY (lossless)' }, { v: 'sog', t: 'SOG (compressed)' }]
        });
        bundleFormatRow.append(bundleFormatSelect);
        const bundleConvertRow = new Container({ class: 'row' });
        bundleConvertRow.append(new Label({ class: 'label', text: 'Convert project splats' }));
        const bundleConvert = new BooleanInput({ value: false });
        bundleConvertRow.append(bundleConvert);
        const bundleReduceRow = new Container({ class: 'row' });
        bundleReduceRow.append(new Label({ class: 'label', text: 'Reduce project SH degree' }));
        const bundleReduce = new BooleanInput({ value: false });
        bundleReduceRow.append(bundleReduce);
        const bundleNote = new Container({ class: 'export-popup-note' });
        bundleNote.append(new Label({
            text: 'Leave both options off to preserve editable splat data. Conversion and SH reduction apply to the exported project, including hidden layers. Your open scene is unchanged.'
        }));

        // viewer: content mode

        const viewerContentRow = new Container({
            class: 'row'
        });

        const viewerContentLabel = new Label({
            class: 'label'
        });
        i18n.bindText(viewerContentLabel, 'popup.export.viewer-content');

        const viewerContentSelect = new SelectInput({
            class: 'select',
            defaultValue: 'standalone'
        });
        i18n.bindOptions(viewerContentSelect, () => [
            { v: 'standalone', t: i18n.t('popup.export.viewer-content.standalone') },
            { v: 'streamed-lod', t: i18n.t('popup.export.viewer-content.streamed-lod') }
        ]);

        viewerContentRow.append(viewerContentLabel);
        viewerContentRow.append(viewerContentSelect);

        // JSI viewer options

        const jsiOptionsHeader = new Label({
            class: 'export-section-header'
        });
        i18n.bindText(jsiOptionsHeader, 'popup.export.jsi-options');

        // viewer: LOD source

        const lodSourceRow = new Container({
            class: 'row'
        });

        const lodSourceLabel = new Label({
            class: 'label'
        });
        i18n.bindText(lodSourceLabel, 'popup.export.lod-source');

        const lodSourceSelect = new SelectInput({
            class: 'select',
            defaultValue: 'generate'
        });
        i18n.bindOptions(lodSourceSelect, () => [
            { v: 'generate', t: i18n.t('popup.export.lod-source.generate') },
            { v: 'visible-splats', t: i18n.t('popup.export.lod-source.visible-splats') }
        ]);

        lodSourceRow.append(lodSourceLabel);
        lodSourceRow.append(lodSourceSelect);

        // viewer: LOD levels

        const lodLevelsRow = new Container({
            class: 'row'
        });

        const lodLevelsLabel = new Label({
            class: 'label'
        });
        i18n.bindText(lodLevelsLabel, 'popup.export.lod-levels');

        const lodLevelsSlider = new SliderInput({
            class: 'slider',
            min: 1,
            max: 4,
            precision: 0,
            value: 4
        });

        lodLevelsRow.append(lodLevelsLabel);
        lodLevelsRow.append(lodLevelsSlider);

        // viewer: LOD decimation

        const lodDecimationRow = new Container({
            class: 'row'
        });

        const lodDecimationLabel = new Label({
            class: 'label'
        });
        i18n.bindText(lodDecimationLabel, 'popup.export.lod-decimation');

        const lodDecimationSlider = new SliderInput({
            class: 'slider',
            min: 10,
            max: 90,
            precision: 0,
            value: 50
        });

        lodDecimationRow.append(lodDecimationLabel);
        lodDecimationRow.append(lodDecimationSlider);

        const lodAssignmentRows: Container[] = [];
        const lodAssignmentSelects: SelectInput[] = [];
        for (let i = 0; i < 4; ++i) {
            const row = new Container({
                class: 'row'
            });
            const label = new Label({
                class: 'label'
            });
            i18n.bindText(label, () => i18n.t('popup.export.lod-assignment', { level: i + 1 }));
            const select = new SelectInput({
                class: 'select',
                defaultValue: `${i}`
            });
            row.append(label);
            row.append(select);
            lodAssignmentRows.push(row);
            lodAssignmentSelects.push(select);
        }

        const lodNote = new Container({
            class: 'export-popup-note'
        });
        const lodNoteText = new Label();
        i18n.bindText(lodNoteText, 'popup.export.lod-note');
        lodNote.append(lodNoteText);

        // viewer: animation

        const animationLabel = new Label({ class: 'label' });
        i18n.bindText(animationLabel, 'popup.export.animation');
        const animationToggle = new BooleanInput({ class: 'boolean', type: 'toggle', value: false });
        const animationRow = new Container({ class: 'row' });
        animationRow.append(animationLabel);
        animationRow.append(animationToggle);

        // viewer: loop mode

        const loopLabel = new Label({ class: 'label' });
        i18n.bindText(loopLabel, 'popup.export.loop-mode');
        const loopSelect = new SelectInput({
            class: 'select',
            defaultValue: 'repeat'
        });
        i18n.bindOptions(loopSelect, () => [
            { v: 'none', t: i18n.t('popup.export.loop-mode.none') },
            { v: 'repeat', t: i18n.t('popup.export.loop-mode.repeat') },
            { v: 'pingpong', t: i18n.t('popup.export.loop-mode.pingpong') }
        ]);
        const loopRow = new Container({ class: 'row' });
        loopRow.append(loopLabel);
        loopRow.append(loopSelect);

        // viewer: clear color

        const colorRow = new Container({
            class: 'row'
        });

        const colorLabel = new Label({
            class: 'label'
        });
        i18n.bindText(colorLabel, 'popup.export.background-color');

        const colorPicker = new ColorPicker({
            class: 'color-picker',
            value: [1, 1, 1, 1]
        });

        colorRow.append(colorLabel);
        colorRow.append(colorPicker);

        // viewer: fov

        const fovRow = new Container({
            class: 'row'
        });

        const fovLabel = new Label({
            class: 'label'
        });
        i18n.bindText(fovLabel, 'popup.export.fov');

        const fovSlider = new SliderInput({
            class: 'slider',
            min: 10,
            max: 120,
            precision: 0,
            value: 60
        });

        fovRow.append(fovLabel);
        fovRow.append(fovSlider);

        const showGuides = new BooleanInput({ class: 'boolean', type: 'toggle', value: false });
        const showGuidesRow = new Container({ class: 'row' });
        showGuidesRow.append(new Label({ class: 'label', text: 'Show measurements and box on load' }));
        showGuidesRow.append(showGuides);
        showGuides.on('change', (showOnLoad: boolean) => {
            const guides = events.invoke('spatialGuides');
            if (showOnLoad !== (guides.showOnLoad ?? false)) events.fire('spatialGuides.update', { ...guides, showOnLoad });
        });

        // viewer: reveal effect
        const revealTypeLabel = new Label({ class: 'label' });
        i18n.bindText(revealTypeLabel, 'popup.export.reveal-effect');
        const revealTypeSelect = new SelectInput({ class: 'select', defaultValue: 'radial' });
        i18n.bindOptions(revealTypeSelect, () => [
            { v: 'none', t: i18n.t('popup.export.reveal-effect.none') },
            { v: 'radial', t: i18n.t('popup.export.reveal-effect.radial') }
        ]);
        const revealTypeRow = new Container({ class: 'row' });
        revealTypeRow.append(revealTypeLabel);
        revealTypeRow.append(revealTypeSelect);

        const revealDurationLabel = new Label({ class: 'label' });
        i18n.bindText(revealDurationLabel, 'popup.export.reveal-duration');
        const revealDurationSlider = new SliderInput({ class: 'slider', min: 0.5, max: 15, precision: 1, value: 1.2 });
        const revealDurationRow = new Container({ class: 'row' });
        revealDurationRow.append(revealDurationLabel);
        revealDurationRow.append(revealDurationSlider);

        const revealPreviewButton = new Button({ class: 'button' });
        i18n.bindText(revealPreviewButton, 'popup.export.reveal-preview');
        const revealPreviewRow = new Container({ class: 'row' });
        revealPreviewRow.append(new Label({ class: 'label', text: '' }));
        revealPreviewRow.append(revealPreviewButton);

        // camera export thinning

        const camerasThinningRow = new Container({ class: 'row' });
        const camerasThinningLabel = new Label({ class: 'label' });
        i18n.bindText(camerasThinningLabel, 'popup.export.cameras-thinning');
        const camerasThinningSelect = new SelectInput({
            class: 'select',
            defaultValue: 'none'
        });
        i18n.bindOptions(camerasThinningSelect, () => [
            { v: 'none', t: i18n.t('popup.export.cameras-thinning.none') },
            { v: 'reduction', t: i18n.t('popup.export.cameras-thinning.reduction') },
            { v: 'target', t: i18n.t('popup.export.cameras-thinning.target') }
        ]);
        camerasThinningRow.append(camerasThinningLabel);
        camerasThinningRow.append(camerasThinningSelect);

        const camerasReductionRow = new Container({ class: 'row' });
        const camerasReductionLabel = new Label({ class: 'label' });
        i18n.bindText(camerasReductionLabel, 'popup.export.cameras-reduction-factor');
        const camerasReductionSlider = new SliderInput({
            class: 'slider',
            min: 1,
            max: 10,
            precision: 0,
            value: 1
        });
        camerasReductionRow.append(camerasReductionLabel);
        camerasReductionRow.append(camerasReductionSlider);

        const camerasTargetRow = new Container({ class: 'row' });
        const camerasTargetLabel = new Label({ class: 'label' });
        i18n.bindText(camerasTargetLabel, 'popup.export.cameras-target-count');
        const camerasTargetSlider = new SliderInput({
            class: 'slider',
            min: 1,
            max: 1,
            precision: 0,
            value: 1
        });
        camerasTargetRow.append(camerasTargetLabel);
        camerasTargetRow.append(camerasTargetSlider);

        // compress

        const compressRow = new Container({
            class: 'row'
        });

        const compressLabel = new Label({
            class: 'label'
        });
        i18n.bindText(compressLabel, 'popup.export.compress-ply');

        const compressBoolean = new BooleanInput({
            class: 'boolean',
            type: 'toggle'
        });

        compressRow.append(compressLabel);
        compressRow.append(compressBoolean);

        const customChannelNote = new Container({
            class: 'export-popup-note'
        });
        const customChannelNoteText = new Label();
        i18n.bindText(customChannelNoteText, 'popup.export.custom-channel-note');
        customChannelNote.append(customChannelNoteText);

        // spherical harmonic bands

        const bandsRow = new Container({
            class: 'row'
        });

        const bandsLabel = new Label({
            class: 'label'
        });
        i18n.bindText(bandsLabel, 'popup.export.sh-bands');

        const bandsSlider = new SliderInput({
            class: 'slider',
            min: 0,
            max: 3,
            precision: 0,
            value: 3
        });

        bandsRow.append(bandsLabel);
        bandsRow.append(bandsSlider);

        // sog iterations

        const iterationsRow = new Container({
            class: 'row'
        });

        const iterationsLabel = new Label({
            class: 'label'
        });
        i18n.bindText(iterationsLabel, 'popup.export.iterations');

        const iterationsSlider = new SliderInput({
            class: 'slider',
            min: 1,
            max: 20,
            precision: 0,
            value: 10
        });

        iterationsRow.append(iterationsLabel);
        iterationsRow.append(iterationsSlider);

        const sogGpuNote = new Container({
            class: 'export-popup-note'
        });
        const sogGpuNoteText = new Label();
        i18n.bindText(sogGpuNoteText, 'popup.export.sog-gpu-note');
        sogGpuNote.append(sogGpuNoteText);

        // spz version

        const spzVersionRow = new Container({
            class: 'row'
        });

        const spzVersionLabel = new Label({
            class: 'label'
        });
        i18n.bindText(spzVersionLabel, 'popup.export.spz-version');

        const spzVersionSelect = new SelectInput({
            class: 'select',
            defaultValue: '4'
        });
        i18n.bindOptions(spzVersionSelect, () => [
            { v: '4', t: i18n.t('popup.export.spz-version.4') },
            { v: '3', t: i18n.t('popup.export.spz-version.3') }
        ]);

        spzVersionRow.append(spzVersionLabel);
        spzVersionRow.append(spzVersionSelect);

        // filename

        const filenameRow = new Container({
            class: 'row'
        });

        const filenameLabel = new Label({
            class: 'label'
        });
        i18n.bindText(filenameLabel, 'popup.export.filename');

        const filenameEntry = new TextInput({
            class: 'text-input'
        });

        filenameRow.append(filenameLabel);
        filenameRow.append(filenameEntry);

        // content

        content.append(viewerTypeRow);
        content.append(bundleFormatRow);
        content.append(bundleConvertRow);
        content.append(bundleReduceRow);
        content.append(bundleNote);
        content.append(viewerContentRow);
        content.append(lodSourceRow);
        content.append(lodLevelsRow);
        content.append(lodDecimationRow);
        lodAssignmentRows.forEach(row => content.append(row));
        content.append(lodNote);
        content.append(jsiOptionsHeader);
        content.append(animationRow);
        content.append(loopRow);
        content.append(colorRow);
        content.append(fovRow);
        content.append(revealTypeRow);
        content.append(showGuidesRow);
        content.append(revealDurationRow);
        content.append(revealPreviewRow);
        content.append(compressRow);
        content.append(customChannelNote);
        content.append(bandsRow);
        content.append(iterationsRow);
        content.append(sogGpuNote);
        content.append(spzVersionRow);
        content.append(camerasThinningRow);
        content.append(camerasReductionRow);
        content.append(camerasTargetRow);
        content.append(filenameRow);

        // footer

        const footer = new Container({ id: 'footer' });

        const cancelButton = new Button({
            class: 'button'
        });
        i18n.bindText(cancelButton, 'popup.cancel');

        const exportButton = new Button({
            class: 'button'
        });
        i18n.bindText(exportButton, 'popup.export');

        footer.append(cancelButton);
        footer.append(exportButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        // handlers

        let onCancel: () => void;
        let onExport: () => void;

        cancelButton.on('click', () => onCancel());
        exportButton.on('click', () => onExport());

        const keydown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    onCancel();
                    break;
                case 'Enter':
                    if (!e.shiftKey) onExport();
                    break;
                default:
                    e.stopPropagation();
                    break;
            }
        };

        const updateExtension = (ext: string) => {
            filenameEntry.value = removeKnownExtension(filenameEntry.value) + ext;
        };

        const updateLodAssignmentOptions = (splatNames: string[]) => {
            const options = splatNames.map((name, index) => ({
                v: `${index}`,
                t: `${index + 1}: ${name}`
            }));
            lodAssignmentSelects.forEach((select, index) => {
                select.options = options;
                select.value = `${Math.min(index, Math.max(0, options.length - 1))}`;
            });
        };

        compressBoolean.on('change', () => {
            updateExtension(compressBoolean.value ? '.compressed.ply' : '.ply');
            customChannelNote.hidden = !compressBoolean.value;
        });

        viewerTypeSelect.on('change', () => {
            updateExtension(viewerTypeSelect.value === 'html' ? '.html' : '.zip');
        });

        const updateViewerContentMode = () => {
            const isStreamedLod = viewerContentSelect.value === 'streamed-lod';
            if (isStreamedLod) {
                viewerTypeSelect.value = 'zip';
            }
            viewerTypeSelect.enabled = !isStreamedLod;
            lodSourceRow.hidden = !isStreamedLod;
            lodLevelsRow.hidden = !isStreamedLod;
            lodDecimationRow.hidden = !isStreamedLod || lodSourceSelect.value !== 'generate';
            lodAssignmentRows.forEach((row, index) => {
                row.hidden = !isStreamedLod || lodSourceSelect.value !== 'visible-splats' || index >= lodLevelsSlider.value;
            });
            lodNote.hidden = !isStreamedLod;
            sogGpuNote.hidden = !isStreamedLod && sogGpuNote.hidden;
            updateExtension(isStreamedLod || viewerTypeSelect.value === 'zip' ? '.zip' : '.html');
        };

        viewerContentSelect.on('change', updateViewerContentMode);
        lodSourceSelect.on('change', updateViewerContentMode);
        lodLevelsSlider.on('change', updateViewerContentMode);

        animationToggle.on('change', (value: boolean) => {
            loopSelect.enabled = value;
        });

        let revealPreviewFrame = 0;
        const stopRevealPreview = () => {
            if (revealPreviewFrame) cancelAnimationFrame(revealPreviewFrame);
            revealPreviewFrame = 0;
            events.invoke('reveal.preview', -1);
        };
        const updateRevealControls = () => {
            const enabled = revealTypeSelect.value !== 'none';
            revealDurationSlider.enabled = enabled;
            revealPreviewButton.enabled = enabled;
            if (!enabled) stopRevealPreview();
        };
        revealTypeSelect.on('change', updateRevealControls);
        revealPreviewButton.on('click', () => {
            stopRevealPreview();
            const start = performance.now();
            const tick = (now: number) => {
                const progress = ((now - start) / 1000 - 0.4) / revealDurationSlider.value;
                if (progress >= 1) {
                    stopRevealPreview();
                } else {
                    events.invoke('reveal.preview', Math.max(0, progress));
                    revealPreviewFrame = requestAnimationFrame(tick);
                }
            };
            events.invoke('reveal.preview', 0);
            revealPreviewFrame = requestAnimationFrame(tick);
        });

        const updateCameraThinningMode = () => {
            const mode = camerasThinningSelect.value as CameraThinningMode;
            camerasReductionRow.hidden = mode !== 'reduction';
            camerasTargetRow.hidden = mode !== 'target';
        };
        camerasThinningSelect.on('change', updateCameraThinningMode);

        let updateCameraPreview: (() => void) | null = null;
        const onCameraThinningChange = () => updateCameraPreview?.();
        camerasThinningSelect.on('change', onCameraThinningChange);
        camerasReductionSlider.on('change', onCameraThinningChange);
        camerasTargetSlider.on('change', onCameraThinningChange);

        let bundleMode = false;
        const updateBundleFormat = () => {
            if (!bundleMode) return;
            bundleFormatSelect.enabled = bundleConvert.value;
            bandsSlider.enabled = bundleReduce.value;
            iterationsRow.hidden = sogGpuNote.hidden = !bundleConvert.value || bundleFormatSelect.value !== 'sog';
        };
        bundleFormatSelect.on('change', updateBundleFormat);
        bundleConvert.on('change', updateBundleFormat);
        bundleReduce.on('change', updateBundleFormat);
        const reset = (exportType: ExportType, splatNames: string[], hasPoses: boolean, poseCount: number) => {
            bundleMode = exportType === 'jsi';
            const allRows = [
                bundleFormatRow, bundleConvertRow, bundleReduceRow, bundleNote, showGuidesRow, revealTypeRow, revealDurationRow, revealPreviewRow,
                viewerTypeRow, viewerContentRow, lodSourceRow, lodLevelsRow, lodDecimationRow, ...lodAssignmentRows, lodNote,
                animationRow, loopRow, colorRow, fovRow, compressRow, customChannelNote, bandsRow, iterationsRow, sogGpuNote, spzVersionRow,
                camerasThinningRow, camerasReductionRow, camerasTargetRow, filenameRow
            ];

            const activeRows = {
                ply: [compressRow, customChannelNote, bandsRow, filenameRow],
                splat: [filenameRow],
                sog: [customChannelNote, bandsRow, iterationsRow, sogGpuNote, filenameRow],
                spz: [customChannelNote, bandsRow, spzVersionRow, filenameRow],
                viewer: [showGuidesRow, viewerTypeRow, viewerContentRow, jsiOptionsHeader, animationRow, loopRow, colorRow, fovRow, revealTypeRow, revealDurationRow, revealPreviewRow, bandsRow, filenameRow],
                settings: [showGuidesRow, revealTypeRow, revealDurationRow, revealPreviewRow, filenameRow],
                cameras: [camerasThinningRow, camerasReductionRow, camerasTargetRow, filenameRow],
                jsi: [bundleFormatRow, bundleConvertRow, bundleReduceRow, bundleNote, bandsRow, filenameRow]
            }[exportType];

            allRows.forEach((r) => {
                r.hidden = activeRows.indexOf(r) === -1;
            });
            jsiOptionsHeader.hidden = exportType !== 'viewer';

            bandsSlider.value = events.invoke('view.bands');
            bandsSlider.enabled = true;

            // ply
            compressBoolean.value = false;
            customChannelNote.hidden = exportType === 'ply' ? true : !['sog', 'spz'].includes(exportType);

            // sog
            iterationsSlider.value = 10;
            bundleFormatSelect.value = 'ply';
            bundleConvert.value = false;
            bundleReduce.value = false;
            updateBundleFormat();

            // viewer LOD
            updateLodAssignmentOptions(splatNames);
            viewerContentSelect.value = 'standalone';
            lodSourceSelect.value = 'generate';
            lodLevelsSlider.value = 4;
            lodDecimationSlider.value = 50;
            viewerTypeSelect.enabled = true;

            // spz
            spzVersionSelect.value = '4';

            camerasThinningSelect.value = 'none';
            camerasReductionSlider.value = 1;
            const targetCountMax = Math.max(1, poseCount);
            // SliderInput has separate limits for its numeric field and its
            // slider track. Keep both in sync when the pose count changes.
            camerasTargetSlider.max = targetCountMax;
            camerasTargetSlider.sliderMax = targetCountMax;
            camerasTargetSlider.value = targetCountMax;
            updateCameraThinningMode();

            // filename
            filenameEntry.value = exportType === 'settings' ? 'settings' : (splatNames[0] ?? '');
            switch (exportType) {
                case 'jsi':
                    updateExtension('.jsi');
                    break;
                case 'ply':
                    updateExtension('.ply');
                    break;
                case 'splat':
                    updateExtension('.splat');
                    break;
                case 'sog':
                    updateExtension('.sog');
                    break;
                case 'spz':
                    updateExtension('.spz');
                    break;
                case 'viewer':
                    updateExtension(viewerTypeSelect.value === 'html' ? '.html' : '.zip');
                    break;
                case 'settings':
                    updateExtension('.json');
                    break;
                case 'cameras':
                    filenameEntry.value = 'cameraPoses';
                    updateExtension('.json');
                    break;
            }

            // viewer
            const bgClr = events.invoke('bgClr');

            animationToggle.value = hasPoses;
            animationToggle.enabled = hasPoses;
            loopSelect.value = events.invoke('timeline.loop') ? 'repeat' : 'none';
            loopSelect.enabled = hasPoses;

            colorPicker.value = [bgClr.r, bgClr.g, bgClr.b];

            fovSlider.value = events.invoke('camera.fov');
            revealTypeSelect.value = 'radial';
            showGuides.value = events.invoke('spatialGuides').showOnLoad ?? false;
            revealDurationSlider.value = 1.2;
            updateRevealControls();

            if (exportType === 'viewer') {
                updateViewerContentMode();
            }
        };

        this.show = (exportType: ExportType, splatNames: string[], showFilenameEdit: boolean) => {
            const frames = events.invoke('timeline.frames');
            const frameRate = events.invoke('timeline.frameRate');
            const smoothness = events.invoke('timeline.smoothness');
            const allPoses = (events.invoke('camera.poses') as Pose[])
            .slice()
            .sort((a, b) => a.frame - b.frame);
            const orderedPoses = allPoses.filter(p => p.frame >= 0 && p.frame < frames);

            reset(exportType, splatNames, orderedPoses.length > 0, allPoses.length);

            updateCameraPreview = exportType === 'cameras' ? () => {
                events.invoke('camera.setPosePreview', thinCameraPoses(
                    allPoses,
                    camerasThinningSelect.value as CameraThinningMode,
                    camerasReductionSlider.value,
                    camerasTargetSlider.value
                ));
            } : null;
            updateCameraPreview?.();

            // filename is only shown in safari where file picker is not supported
            filenameRow.hidden = !showFilenameEdit;

            this.hidden = false;
            events.fire('exportPopup.visible', true);
            this.dom.addEventListener('keydown', keydown);
            this.dom.focus();

            const assemblePlyOptions = () : SceneExportOptions => {
                return {
                    filename: filenameEntry.value,
                    splatIdx: 'all',
                    serializeSettings: {
                        maxSHBands: bandsSlider.value
                    },
                    compressedPly: compressBoolean.value
                };
            };

            const assembleSplatOptions = () : SceneExportOptions => {
                return {
                    filename: filenameEntry.value,
                    splatIdx: 'all',
                    serializeSettings: { }
                };
            };

            const assembleSogOptions = () : SceneExportOptions => {
                return {
                    filename: filenameEntry.value,
                    splatIdx: 'all',
                    serializeSettings: {
                        maxSHBands: bandsSlider.value
                    },
                    sogIterations: iterationsSlider.value
                };
            };

            const assembleSpzOptions = () : SceneExportOptions => {
                return {
                    filename: filenameEntry.value,
                    splatIdx: 'all',
                    serializeSettings: {
                        maxSHBands: bandsSlider.value
                    },
                    spzVersion: spzVersionSelect.value === '3' ? 3 : 4
                };
            };

            const assembleViewerOptions = () : SceneExportOptions => {
                const fov = fovSlider.value;

                // use current viewport as start pose
                const pose = events.invoke('camera.getPose');
                const p = pose?.position;
                const t = pose?.target;
                const cameras = (p && t) ? [{
                    initial: {
                        position: [p.x, p.y, p.z] as [number, number, number],
                        target: [t.x, t.y, t.z] as [number, number, number],
                        fov
                    }
                }] : [];

                const includeAnimation = animationToggle.value;
                const animTracks: AnimTrack[] = [];

                if (includeAnimation && orderedPoses.length > 0) {
                    const times: number[] = [];
                    const position: number[] = [];
                    const target: number[] = [];
                    const fovKeys: number[] = [];
                    for (let i = 0; i < orderedPoses.length; ++i) {
                        const op = orderedPoses[i];
                        times.push(op.frame);
                        position.push(op.position.x, op.position.y, op.position.z);
                        target.push(op.target.x, op.target.y, op.target.z);
                        fovKeys.push(op.fov ?? fov);
                    }

                    animTracks.push({
                        name: 'cameraAnim',
                        duration: frames / frameRate,
                        frameRate,
                        loopMode: loopSelect.value as 'none' | 'repeat' | 'pingpong',
                        interpolation: 'spline',
                        smoothness,
                        keyframes: {
                            times,
                            values: { position, target, fov: fovKeys }
                        }
                    });
                }

                const bgColor = colorPicker.value.slice(0, 3) as [number, number, number];

                const posesToExport = exportType === 'cameras' ? thinCameraPoses(
                    allPoses,
                    camerasThinningSelect.value as CameraThinningMode,
                    camerasReductionSlider.value,
                    camerasTargetSlider.value
                ) : allPoses;

                const viewerDefaults = events.invokeOptional('sceneData.viewerDefaults') ?? {};
                const experienceSettings: ExperienceSettings = {
                    version: 2,
                    tonemapping: 'none',
                    highPrecisionRendering: false,
                    postEffectSettings: defaultPostEffectSettings(),
                    ...viewerDefaults,
                    background: { color: bgColor },
                    animTracks,
                    cameras,
                    annotations: events.invoke('annotations') ?? [],
                    spatialGuides: { ...events.invoke('spatialGuides'), showOnLoad: showGuides.value },
                    cameraPoses: posesToExport.map((pose, i) => ({
                        ...pose,
                        id: pose.id ?? pose.name ?? `camera_${i}`,
                        position: [pose.position.x, pose.position.y, pose.position.z] as [number, number, number],
                        target: [pose.target.x, pose.target.y, pose.target.z] as [number, number, number],
                        fov: pose.fov ?? fov,
                        thumbnailUrl: exportedThumbnailUrl(pose.thumbnailUrl),
                        imageUrl: pose.imageUrl,
                        label: pose.label ?? pose.name,
                        group: pose.group
                    })),
                    cameraPosesSpace: 'viewer',
                    cameraPosesTransform: events.invoke('camera.poseTransform'),
                    cameraPosesScale: viewerDefaults.cameraPosesScale ?? 2.5,
                    cameraPosesLineWidth: viewerDefaults.cameraPosesLineWidth ?? 0.04,
                    cameraPosesOpacity: viewerDefaults.cameraPosesOpacity ?? 0.45,
                    cameraPosesInactiveOpacity: viewerDefaults.cameraPosesInactiveOpacity ?? 0.15,
                    revealEffect: {
                        type: revealTypeSelect.value as 'none' | 'radial',
                        duration: revealDurationSlider.value,
                        delay: 0.4
                    },
                    startMode: includeAnimation ? 'animTrack' : 'default'
                };

                return {
                    filename: filenameEntry.value,
                    splatIdx: 'all',
                    serializeSettings: {
                        maxSHBands: bandsSlider.value
                    },
                    viewerExportSettings: {
                        type: viewerContentSelect.value === 'streamed-lod' ? 'zip' : viewerTypeSelect.value as 'html' | 'zip',
                        content: viewerContentSelect.value as 'standalone' | 'streamed-lod',
                        lodSource: lodSourceSelect.value as 'generate' | 'visible-splats',
                        lodLevels: lodLevelsSlider.value,
                        lodDecimationPercent: lodDecimationSlider.value,
                        lodSplatIndices: lodAssignmentSelects
                        .slice(0, lodLevelsSlider.value)
                        .map(select => parseInt(select.value, 10))
                        .filter(index => Number.isFinite(index)),
                        experienceSettings
                    }
                };
            };

            return new Promise<null | SceneExportOptions>((resolve) => {
                onCancel = () => {
                    stopRevealPreview();
                    resolve(null);
                };

                onExport = () => {
                    stopRevealPreview();
                    switch (exportType) {
                        case 'jsi':
                            resolve({
                                filename: filenameEntry.value,
                                splatIdx: 'all',
                                bundleFormat: bundleFormatSelect.value as 'ply' | 'sog',
                                bundleConvert: bundleConvert.value,
                                bundleReduceSH: bundleReduce.value,
                                serializeSettings: { maxSHBands: bandsSlider.value },
                                sogIterations: iterationsSlider.value
                            });
                            break;
                        case 'ply':
                            resolve(assemblePlyOptions());
                            break;
                        case 'splat':
                            resolve(assembleSplatOptions());
                            break;
                        case 'sog':
                            resolve(assembleSogOptions());
                            break;
                        case 'spz':
                            resolve(assembleSpzOptions());
                            break;
                        case 'viewer':
                            resolve(assembleViewerOptions());
                            break;
                        case 'settings':
                            resolve(assembleViewerOptions());
                            break;
                        case 'cameras':
                            resolve(assembleViewerOptions());
                            break;
                    }
                };
            }).finally(() => {
                updateCameraPreview = null;
                events.invoke('camera.setPosePreview', null);
                this.dom.removeEventListener('keydown', keydown);
                this.hide();
            });
        };

        this.hide = () => {
            this.hidden = true;
            events.fire('exportPopup.visible', false);
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { ExportPopup };
